// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Lustre — the three modes.
//
// A mode must change how the game PLAYS, not just a number. These do:
//   OPEN WHEEL   the reference game — build a bench, convert it to score at a public price.
//   DRY LODE     the grit you spend is partly DESTROYED, so the economy decays under the table and
//                a second verb (PAN) recovers from the spoil heap at a FLAT rate — your engine buys
//                you nothing there, which throttles a big engine and a small one identically.
//   COMMISSION   free-form conversion is gone. There is no CUT. Ten fixed, contested targets are the
//                only score, and taking one costs ticks AND burns two of your rough spars.
//
// modeOf() validates an id off the wire. `MODES[id] || DEFAULT` would let "constructor" through as a
// Mode of undefined fields, and an undefined CLOSE reaches the generator as NaN.

export type ModeId = 'open' | 'dry' | 'commission';

export interface Mode {
  id: ModeId;
  name: string;
  blurb: string;
  /** Ticks on the Wheel. The Close is the only ending. */
  close: number;
  /** Dry Lode only: the first grit of every payment is destroyed instead of returning to the pool. */
  destroys: boolean;
  /** Dry Lode only: PAN recovers 2 grit from the Tailings, at a flat 2 ticks. */
  pan: boolean;
  /** Commission only: no CUT verb; Patrons are the score and cost the band price. */
  patronIsVerb: boolean;
  /** Face-up Patrons. Core/Dry deal P+2 and allow one each; Commission deals 10, no per-player cap. */
  patronsUp: (players: number) => number;
  /** Core/Dry: one Patron per player, ever. */
  patronCap: number;
  /** The steep half of the band ladder. Dry Lode's tail is steeper — liquidity is already scarce. */
  tailStep: number;
  /**
   * How much banked Lustre buys one band of price, as a multiple of `max(2, players - 1)`.
   *
   * TUNING KNOB ZERO, and the only number in the game that was chosen by measurement rather than by
   * argument. At 3 the ladder was so slack that every bot in every mode converted at almost exactly
   * the same tick (first-cut median 30 of 48, IQR 4): the mid-game "when do I stop building" decision
   * did not exist, because holding was free. Lowering it makes the price outrun a hoarder.
   * tests/balance.test.ts pins the resulting first-cut spread; do not edit this without re-running
   * the sweep in tests/mechanism.test.ts.
   */
  bandMult: number;
  /**
   * How many ROUGH spars fit on a bench. A cut frees a slot, so this is the second half of the
   * conversion decision — and, it turns out, the important half.
   *
   * MEASURED. With a slack bench the only thing that ever forces a conversion is the clock, and
   * every seat carries the same clock, so every bot in the game converted within a couple of ticks
   * of every other: first-cut IQR 2-8 no matter where tuning knob zero was set. A bench that fills
   * binds at a time each player chooses for themselves, which is what turns "when do I stop
   * building" from a phase transition into a decision. tests/balance.test.ts pins the spread.
   */
  roughCap: number;
  /**
   * Divides every Patron's pip requirement (rounded up).
   *
   * MEASURED. Commission has no CUT verb, so a Patron is the only thing that scores — and at the
   * full requirement of six pips a player managed 0.6 of them in a whole game and the mode finished
   * on a mean of 2.6 with a third of games tied. Halved, an order is three or four spars, which is
   * three or four races per game instead of one crawl. It is the same fourteen cards at the same
   * values; only the bar moves.
   */
  patronDiv: number;
  /**
   * Grit staked to each player per place they are down the starting order — first actor none, second
   * one lot, and so on.
   *
   * MEASURED, and it is a compensation rather than a reorder on purpose: reordering an advantage
   * moves it somewhere you did not measure, while paying it off cancels it where it actually lands.
   * Dry Lode needed it most — destroying grit makes the pool poorer for everyone who comes after
   * you, so acting first compounded into a 74% win rate at two players. Swept per mode; Open Wheel
   * wants less than Dry Lode does, and that difference is the modes being genuinely different rather
   * than a constant nobody checked.
   *
   * Indexed BY PLAYER COUNT (2..4), because a single number per mode does not fit. Dry Lode destroys
   * one grit per claim, so the pool is poorer by the time your turn comes round and the damage grows
   * with the number of players ahead of you: at 2 players a stake of 1.5 was right and at 4 it left
   * the first actor winning 38% against a fair 25%, while the value that fixed 4 players overshot 2
   * players to 42%. Open Wheel destroys nothing and wants the same number throughout.
   */
  stake: number[];
}

/** Stake for this table size, clamped — a wire-supplied player count must never index off the end. */
export const stakeFor = (mode: Mode, players: number): number =>
  mode.stake[Math.max(0, Math.min(mode.stake.length - 1, Math.floor(players) - 2))] ?? 0;

export const MODES: Record<ModeId, Mode> = {
  open: {
    id: 'open',
    name: 'Open Wheel',
    blurb: 'The reference game. Build a bench, then decide when to spend it.',
    close: 48,
    destroys: false,
    pan: false,
    patronIsVerb: false,
    patronsUp: (p) => p + 2,
    patronCap: 1,
    tailStep: 2,
    bandMult: 2,
    roughCap: 6,
    patronDiv: 1,
    stake: [1, 1, 1],
  },
  dry: {
    id: 'dry',
    name: 'Dry Lode',
    blurb: 'The seam runs cold. Grit you spend is half lost — pan the spoil to get it back.',
    close: 44,
    destroys: true,
    pan: true,
    patronIsVerb: false,
    patronsUp: (p) => p + 2,
    patronCap: 1,
    tailStep: 3,
    bandMult: 2,
    roughCap: 6,
    patronDiv: 1,
    stake: [1.5, 2, 3],
  },
  commission: {
    id: 'commission',
    name: 'Commission',
    blurb: 'No free cutting. Ten named orders, and you burn two spars to fill one.',
    close: 44,
    destroys: false,
    pan: false,
    patronIsVerb: true,
    patronsUp: () => 10,
    patronCap: 99,
    tailStep: 2,
    bandMult: 2,
    roughCap: 6,
    patronDiv: 2,
    stake: [1, 1, 1],
  },
};

export const MODE_IDS: ModeId[] = ['open', 'dry', 'commission'];
export const DEFAULT_MODE: ModeId = 'open';

/** Validate an id that arrived over the wire or out of localStorage. Never index a raw string. */
export function modeOf(id: unknown): Mode {
  return typeof id === 'string' && Object.hasOwn(MODES, id) ? MODES[id as ModeId] : MODES[DEFAULT_MODE];
}
